import {
  Badge,
  Card,
  Text,
  tokens,
  ProgressBar,
} from '@fluentui/react-components';
import {
  ChartMultipleRegular,
  LightbulbRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import type { AnalyticsTabProps } from './types';
import { CardSkeleton } from '../../components/LoadingSkeleton';

export default function AnalyticsTab({
  analytics,
  analyticsLoading,
}: AnalyticsTabProps) {
  return (
    <div>
      {analyticsLoading ? (
        <div style={{ padding: '48px' }}>
          <CardSkeleton />
        </div>
      ) : analytics ? (
        <div>
          {/* Key Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold" style={{ color: tokens.colorBrandForeground1 }}>
                {analytics.totalExtractions}
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Total Extractions
              </Text>
            </Card>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold" style={{ color: analytics.correctionRate > 0.1 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
                {(analytics.correctionRate * 100).toFixed(1)}%
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Correction Rate
              </Text>
            </Card>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                {(analytics.autoMatchRate * 100).toFixed(1)}%
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Auto-Match Rate
              </Text>
            </Card>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold" style={{ color: tokens.colorBrandForeground1 }}>
                {(analytics.averageConfidence * 100).toFixed(0)}%
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Avg Confidence
              </Text>
            </Card>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold">
                {(analytics.averageProcessingTimeMs / 1000).toFixed(1)}s
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Avg Processing Time
              </Text>
            </Card>
            <Card style={{ padding: '16px', textAlign: 'center' }}>
              <Text size={800} weight="bold" style={{ color: tokens.colorPaletteMarigoldForeground1 }}>
                {analytics.totalCorrections}
              </Text>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Total Corrections
              </Text>
            </Card>
          </div>

          {/* Correction Patterns */}
          {analytics.correctionPatterns && analytics.correctionPatterns.length > 0 && (
            <Card style={{ padding: '20px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <LightbulbRegular style={{ fontSize: tokens.fontSizeBase500, color: tokens.colorPaletteMarigoldForeground1 }} />
                <Text weight="semibold" size={500}>Detected Patterns & Improvement Suggestions</Text>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {analytics.correctionPatterns.map((pattern, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '12px',
                      backgroundColor: tokens.colorNeutralBackground3,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${tokens.colorPaletteMarigoldForeground1}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Text weight="semibold">{pattern.description}</Text>
                        <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                          Occurred {pattern.occurrences} times • Affects: {pattern.affectedFields.join(', ')}
                        </Text>
                      </div>
                      <Badge appearance="outline" color="warning">{pattern.occurrences}x</Badge>
                    </div>
                    {pattern.suggestedFix && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: tokens.colorNeutralBackground1,
                        borderRadius: '4px',
                      }}>
                        <Text size={200} style={{ color: tokens.colorBrandForeground1 }}>
                          Suggested Fix: {pattern.suggestedFix}
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Corrections by Field */}
          {analytics.correctionsByField && analytics.correctionsByField.length > 0 && (
            <Card style={{ padding: '20px', marginBottom: '24px' }}>
              <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: '16px' }}>
                Corrections by Field
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {analytics.correctionsByField.slice(0, 10).map((field, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text size={300}>{field.field}</Text>
                      <ProgressBar
                        value={field.percentage / 100}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                    <div style={{ textAlign: 'right', minWidth: '80px' }}>
                      <Text weight="semibold">{field.count}</Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        ({field.percentage.toFixed(1)}%)
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Prompt Version Stats */}
          {analytics.promptVersionStats && analytics.promptVersionStats.length > 0 && (
            <Card style={{ padding: '20px' }}>
              <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: '16px' }}>
                Prompt Version Performance
              </Text>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                {analytics.promptVersionStats.map((version, i) => (
                  <Card
                    key={i}
                    style={{
                      padding: '16px',
                      backgroundColor: tokens.colorNeutralBackground3,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <Badge appearance="filled" color="brand">v{version.version}</Badge>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {version.extractionCount} extractions
                      </Text>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Corrections</Text>
                        <Text as="p" weight="semibold">{version.correctionCount}</Text>
                      </div>
                      <div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Correction Rate</Text>
                        <Text as="p" weight="semibold" style={{
                          color: version.correctionRate > 0.1 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1
                        }}>
                          {(version.correctionRate * 100).toFixed(1)}%
                        </Text>
                      </div>
                      <div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Avg Confidence</Text>
                        <Text as="p" weight="semibold">{(version.averageConfidence * 100).toFixed(0)}%</Text>
                      </div>
                      <div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Last Used</Text>
                        <Text as="p" weight="semibold">
                          {version.lastUsed ? format(new Date(version.lastUsed), 'MMM d') : '-'}
                        </Text>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          )}

          {/* Empty State */}
          {analytics.totalExtractions === 0 && (
            <Card style={{ padding: '48px', textAlign: 'center' }}>
              <ChartMultipleRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
              <Text as="p" style={{ marginTop: '16px' }}>No analytics data yet</Text>
              <Text as="p" style={{ color: tokens.colorNeutralForeground3 }}>
                Upload Drake PDFs to start collecting analytics
              </Text>
            </Card>
          )}
        </div>
      ) : (
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <ChartMultipleRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
          <Text as="p" style={{ marginTop: '16px' }}>Unable to load analytics</Text>
        </Card>
      )}
    </div>
  );
}
